import {
	ChangeDetectionStrategy,
	Component,
	inject,
	input,
	OnInit,
	signal,
	ViewChild,
} from '@angular/core';
import { fade, openClose } from '@core/animations/animations';
import { Tenant } from '@models/matter';
import { UntilDestroy } from '@ngneat/until-destroy';
import { createId } from 'src/app/shared/core/util/file';
import { ICON } from 'src/app/shared/core/util/icon';
import { MatterDispatch } from 'src/app/shared/state/matter';
import { SelectedMatter } from 'src/app/shared/state/matter/matter.selectors';
import { TestHooks } from './tenants-form.hooks';
import { InputComponent } from 'src/app/shared/atomic/atoms/input/input.component';
import { ModalService } from 'src/app/ui/modal/modal.service';
import { select } from '@ngxs/store';

@UntilDestroy()
@Component({
	selector: 'app-tenants-form',
	templateUrl: './tenants-form.component.html',
	styles: [
		`
			.no-container {
				border: none !important;
			}

			::ng-deep {
				.no-container > .container__content {
					padding: 0 !important;
				}
			}
		`,
	],
	animations: [fade(), openClose()],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class TenantsFormComponent implements OnInit {
	testHooks = TestHooks;
	ICON = ICON;
	tenant_ = input<Tenant>();
	form_ = signal<Tenant>({});
	isEditing_ = signal<boolean>(false);
	showForm_ = signal<boolean>(false);
	matterId_ = select(SelectedMatter.id);
	@ViewChild('fullName') fullName!: InputComponent;
	private matterDispatch = inject(MatterDispatch);
	private modalService = inject(ModalService);

	ngOnInit(): void {
		if (this.tenant_()) {
			this.isEditing_.set(true);
			this.form_.set(this.tenant_());
			this.openForm();
		}
	}

	add(): void {
		this.createForm();
		this.openForm();
	}

	openForm(): void {
		this.showForm_.set(true);
		setTimeout(() => {
			this.fullName?.inputElementRef?.nativeElement?.focus();
		});
	}

	cancel(): void {
		this.isEditing_()
			? this.modalService.closeModal()
			: this.showForm_.set(false);
	}

	save(): void {
		if (!this.matterId_() || !this.form_()?.fullName) return;

		this.isEditing_()
			? this.matterDispatch.UpdateTenant(this.matterId_(), this.form_())
			: this.matterDispatch.AddTenant(this.matterId_(), this.form_());

		this.cancel();
	}

	createForm(): void {
		this.form_.set({
			id: createId(),
			fullName: null,
			phoneNumber: null,
			leaseStartDate: null,
			leaseEndDate: null,
			rentAmount: null,
			depositAmount: null,
		});
	}

	updateField(value: string, field: keyof Tenant): void {
		if (!this.matterId_()) return;

		const updatedTenant: Tenant = {
			...this.form_(),
			[field]: value,
		};

		this.form_.set(updatedTenant);
	}
}
